import { useState, useEffect, useMemo } from "react";
import { ReactComponent as SpinnerIcon } from "../../../assets/img/icons/spinner.svg";
import { useSchoolApi, useMetricApi } from "../../../Hooks";
import { Table } from "../../Shared/Table";
import { Pagination } from "../../Shared/Pagination";
import Dropdown from '../../Shared/Dropdown';
import { useTranslation } from "react-i18next";

export default function ProgressosCursos() {
  const [loading, setLoading] = useState(true);
  const [courseProgresses, setCourseProgresses] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [listData, setListData] = useState({
    page: 1,
    pageCount: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const schoolApi = useSchoolApi();
  const metricApi = useMetricApi();

  const [schools, setSchools] = useState([]);
  const [schoolFilter, setSchoolFilter] = useState(null);
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith("en");
  const selectSchoolLabel = t("selectSchool", {
    defaultValue: isEnglish ? "Select a School" : "Selecione uma Escola",
  });
  const selectCityWithSchoolsLabel = t("selectCityWithSchools", {
    defaultValue: isEnglish ? "Select a city with schools" : "Selecione uma cidade com escolas",
  });
  const exportToExcelLabel = t("exportToExcel", {
    defaultValue: isEnglish ? "Export to Excel" : "Exportar para Excel",
  });
  const notAvailableLabel = t("notAvailable", {
    defaultValue: isEnglish ? "Not available" : "Não consta",
  });
  const classesLabel = t("classes", {
    defaultValue: isEnglish ? "Classes" : "Turmas",
  });
  const registrationDateLabel = t("registrationDate", {
    defaultValue: isEnglish ? "Registration Date" : "Data de Cadastro",
  });
  const nameLabel = t("name", {
    defaultValue: isEnglish ? "Name" : "Nome",
  });
  const emailLabel = t("email", {
    defaultValue: "E-mail",
  });

  const buildReportFileName = (formattedDate) =>
    t("progressReportFileName", {
      date: formattedDate,
      defaultValue: isEnglish
        ? `Course_Progress_Report_${formattedDate}.xlsx`
        : `Relatorio_Progresso_${formattedDate}.xlsx`,
    });

  const getCurrentLanguage = () => localStorage.getItem("language") || "pt-BR";
  const getClassCredentials = () => {
    if (typeof window === "undefined") {
      return { login: null, token: null, role: null };
    }

    return {
      login: localStorage.getItem("email"),
      token: localStorage.getItem("tokenClass"),
      role: localStorage.getItem("classRole"),
    };
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await loadSchools();
        await new Promise((r) => setTimeout(r, 500));
        await reloadData(1, null, true);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) {
      reloadData(listData.page, schoolFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const loadSchools = async () => {
    try {
      const { login, token, role } = getClassCredentials();
      const response = await schoolApi.courseReportSchools(login, token, role);
      const mappedSchools = response.map((school) => ({
        value: school.id,
        label: school.name,
      }));
      setSchools(mappedSchools);
    } catch (error) {
      console.error("Error loading schools for course report:", error);
      setSchools([]);
    }
  }

  const reloadData = async (page, selectedSchool = null, skipLoader = false) => {
    try {
      if (!skipLoader) {
        setLoadingData(true);
      }

      const { login, token, role } = getClassCredentials();
      const schoolFilterValue = selectedSchool ? selectedSchool.value : "";
      const language = getCurrentLanguage();

      const getCourseProgresses = await metricApi.courseProgressesBySchoolList(
        page,
        listData.itemsPerPage,
        schoolFilterValue,
        language,
        login,
        token,
        role
      );

      setCourseProgresses(getCourseProgresses.items);
      setListData((prev) => ({
        ...prev,
        page,
        pageCount: getCourseProgresses.pageCount,
        totalItems: getCourseProgresses.totalItems,
      }));
    } catch (error) {
      console.error("Error during data reload:", error);
    } finally {
      if (!skipLoader) {
        setLoadingData(false);
      }
    }
  };

  const handleSchoolFilterChange = async (e) => {
    setSchoolFilter(e);
    await reloadData(1, e);
  }


  const handlePageClick = (event) => {
    reloadData(event.selected + 1, schoolFilter);
  };

  const handleGenerateReportCourseProgress = async () => {
    const schoolFilterValue = schoolFilter ? schoolFilter.value : "";
    const language = getCurrentLanguage();
    const { login, token, role } = getClassCredentials();
    const getCourseProgresses = await metricApi.generateReportProgressesSchool(
      schoolFilterValue,
      language,
      login,
      token,
      role
    );

    const date = new Date();
    const formattedDate = `${date.getDate()
      .toString()
      .padStart(2, "0")}_${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}_${date.getFullYear()}`;
    const fileName = buildReportFileName(formattedDate);

    const url = window.URL.createObjectURL(new Blob([getCourseProgresses]));
    const link = document.createElement('a');
    link.href = url;

    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const columns = useMemo(() => {
    if (courseProgresses.length === 0) return [];
    const exampleRow = courseProgresses[0];

    const headerTranslations = {
      Turma: classesLabel,
      Classes: classesLabel,
      "Data cadastro": registrationDateLabel,
      "Data Cadastro": registrationDateLabel,
      "Registration Date": registrationDateLabel,
      Nome: nameLabel,
      Name: nameLabel,
      "E-mail": emailLabel,
      Email: emailLabel,
    };

    const normalizeValue = (value) => {
      if (value === null || value === undefined || value === "") {
        return "0%";
      }

      if (typeof value === "string") {
        const trimmedValue = value.trim();
        const normalizedValue = trimmedValue
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();

        if (normalizedValue === "nao consta") {
          return notAvailableLabel;
        }

        if (normalizedValue === "not available") {
          return notAvailableLabel;
        }

        return trimmedValue;
      }

      return value;
    };

    const columnsKeys = Object.keys(exampleRow);

    return columnsKeys.map((columnKey) => {
      const header = headerTranslations[columnKey] || columnKey.replace(/_/g, " ").toUpperCase();

      return {
        Header: header,
        accessor: columnKey,
        Cell: ({ value }) => {
          const displayValue = normalizeValue(value);
          const displayString = displayValue?.toString() ?? "";
          return (
            <p data-tip={displayString}>{displayString}</p>
          );
        },
        width: "20%",
      };
    });
  }, [courseProgresses, classesLabel, registrationDateLabel, nameLabel, emailLabel, notAvailableLabel]);


  return (
    <div className="flex flex-col">
      {loading ? (
        <SpinnerIcon className="m-auto" />
      ) : (
        <div className="-ml-12 w-screen md:w-full md:ml-0 flex flex-col">
          <div className="flex flex-col flex-wrap items-center md:flex-row my-3 gap-y-3 gap-x-3">
            <Dropdown
              items={schools}
              value={schoolFilter}
              placeholder={selectSchoolLabel}
              emptyMessage={selectCityWithSchoolsLabel}
              onChange={handleSchoolFilterChange}
            />
            <div className="lg:ml-auto flex flex-col flex-wrap items-center md:flex-row mt-3 gap-y-3 gap-x-3">
              <button
                onClick={handleGenerateReportCourseProgress}
                disabled={schoolFilter == null || schoolFilter.value == ""}
                className={`flex flex-row items-center border-b-2 font-inter rounded w-36 py-2 ${
                  schoolFilter == null || schoolFilter.value == ""
                    ? "bg-gray-400 border-gray-400 text-gray-700 cursor-not-allowed"
                    : "bg-playmove-mediumblue border-playmove-blue text-white hover:bg-playmove-blue"
                }`}
              >
                <span className="m-auto flex flex-row items-center gap-x-1"><span className="text-sm font-bold text-white">{exportToExcelLabel}</span></span>
              </button>
            </div>
          </div>
          <Table className="w-full" columns={columns} data={courseProgresses} loadingContent={loadingData} />
          <Pagination
            className="ml-auto mt-3"
            pageCount={listData.pageCount}
            currentPage={listData.page - 1}
            handlePageClick={handlePageClick}
          />
        </div>
      )}
    </div>
  );
}
